/**
 * PRODUCTO DEL SORTEO ACTIVO — la única autoridad sobre QUÉ está mostrando el
 * front: receta, config de rueda y tema, coherentes por construcción.
 *
 * QUÉ resuelve: un front mono-sorteo (customer/tv/game) muestra UN producto a
 * la vez — recarga al cambiar de sorteo. Antes, cada componente shared (wheel,
 * mat, tiquete, stats-panel, i18n) recibía la rueda por un mecanismo propio y
 * en DOS formas distintas (receta cruda vs. config materializada), y el tema
 * viajaba aparte por otro setter: podían desfasarse y de hecho se desfasaban.
 *
 * Este módulo es el punto único: el front FIJA el producto una vez en el
 * bootstrap (y de nuevo al cambiar de sorteo o de tema); los componentes shared
 * mono-producto (i18n, mat, stats-panel) lo OBTIENEN directo, y los que trabajan
 * por producto (Tiquete, la caja) lo reciben por parámetro en cada llamada.
 * Ningún componente guarda un producto por setter propio.
 *
 * Las dos formas de la rueda NO son intercambiables y no se fingen: la RECETA
 * (catálogo declarativo — `apuestas`, `sectores`, `mat.botones`, `barrasDeStats`)
 * la leen i18n, lector, prize-table y stats; la CONFIG materializada (métodos
 * `obtenerApuestaPorId`, `obtenerClaveDePaletaDeSector`, geometría) la usan el
 * money-path, el mat y el tiquete. El producto lleva las dos, del mismo tipo.
 *
 * Quien muestra VARIOS productos a la vez —la caja: "ver sorteos" con uno y el
 * mat con otro; el historial del lobby— no fija el global: construye su
 * producto con `crearProductoDeRueda` y lo pasa explícito a cada consumidor.
 *
 * NO es el global mutable que B-front mató (`_activeConfig`/`setActiveType` del
 * SSOT): aquel lo mutaba el money-path del backend, donde N sorteos liquidan en
 * paralelo. Éste es estado del FRONT mono-sorteo. El backend no usa este módulo.
 */

import { crearConfiguracionDeRuedaParaTipo } from './rueda-config-factory.js';
import { RECETAS_AGNOSTICAS } from './recetas-agnosticas.js';
import { normalizarClaveDeTipoDeRueda } from './roulette-numbers-order-config.js';

// Producto del sorteo activo. `null` hasta que el front lo fija en el bootstrap.
let _productoDelSorteoActivo = null;

/**
 * Construye un producto de rueda: receta del catálogo + config materializada
 * (cacheada por la factory) + tema visual, todos del mismo tipo. Inmutable.
 *
 * @param {Object} args
 * @param {string} args.tipoDeRueda - id del tipo ('european', 'rueda10_american', ...).
 * @param {Object|null} [args.tema] - tema tal como lo devuelve `AppThemes.get`
 *   (con `paletas['paleta-N'].nombreI18nKey`), o `null` si el sorteo no lo declara.
 * @returns {{ tipoDeRueda: string, receta: Object, configDeRueda: Object, tema: Object|null }}
 * @throws {Error} si el tipo no existe — fail-fast de la factory.
 */
export function crearProductoDeRueda({ tipoDeRueda, tema = null }) {
    // Una sola normalización ('EUROPEAN' → 'european') y la misma clave para
    // las tres facetas: si se normalizara solo en la factory, la receta se
    // buscaría con la clave cruda y el producto nacería incoherente.
    const claveCanonicaDeTipo = normalizarClaveDeTipoDeRueda(tipoDeRueda);
    const configDeRueda = crearConfiguracionDeRuedaParaTipo(claveCanonicaDeTipo);
    return Object.freeze({
        tipoDeRueda: claveCanonicaDeTipo,
        receta: RECETAS_AGNOSTICAS[claveCanonicaDeTipo],
        configDeRueda,
        tema,
    });
}

/**
 * Fija el producto que el front va a mostrar. La llama el front en el bootstrap
 * y cada vez que cambia el tipo de rueda o el tema del sorteo.
 *
 * @param {{ tipoDeRueda: string, tema?: Object|null }} args - ver `crearProductoDeRueda`.
 */
export function fijarProductoDelSorteoActivo(args) {
    _productoDelSorteoActivo = crearProductoDeRueda(args);
}

/**
 * Producto del sorteo activo. Fail-fast: lanza si el front no lo fijó todavía —
 * un componente que lo pide antes del bootstrap es un bug de orden, no debe
 * operar con datos a medias.
 */
export function obtenerProductoDelSorteoActivo() {
    if (_productoDelSorteoActivo === null) {
        throw new Error(
            '[ProductoDelSorteoActivo] No hay producto fijado — un componente lo pidió antes de que ' +
                'el front llamara fijarProductoDelSorteoActivo() en el bootstrap. Bug de orden de arranque.'
        );
    }
    return _productoDelSorteoActivo;
}

/** Faceta RECETA del producto activo (catálogo declarativo). */
export function obtenerRecetaDelSorteoActivo() {
    return obtenerProductoDelSorteoActivo().receta;
}

/** Faceta CONFIG materializada del producto activo (`crearConfiguracionDeRuedaParaTipo`). */
export function obtenerRuedaDelSorteoActivo() {
    return obtenerProductoDelSorteoActivo().configDeRueda;
}

/**
 * ¿Ya se fijó el producto? Para barreras de arranque que esperan la config sin
 * provocar el throw de `obtenerProductoDelSorteoActivo()`.
 */
export function hayRuedaDelSorteoActivoFijada() {
    return _productoDelSorteoActivo !== null;
}
